import { useState } from 'react';
import { Braces, KeyRound, Webhook } from 'lucide-react';
import api from '../../../utils/api';
import { PartnerEndpoints } from '../../../utils/endpoints';
import { Permissions } from '../../../utils/permissions';
import { useAuth } from '../../../AuthContext';
import usePartnerDetails from '../../../hooks/usePartnerDetails';
import PageHeader from '../../shell/PageHeader';
import { Card, Button, Alert, EmptyState, Skeleton } from '../../ui';
import ApiSettings from '../partials/ApiSettings';
import WebhookSecret from '../partials/WebhookSecret';
import WebHooks from './WebHooks';
import WhitelistedIps from './WhitelistedIps';
import EditWebhookModal from '../modals/EditWebhookModal';

const Developer = () => {
  const { hasPermission } = useAuth();
  const { details, loading, error, setError, refetch } = usePartnerDetails();
  const [generating, setGenerating] = useState(false);
  const [webhookModalOpen, setWebhookModalOpen] = useState(false);

  const handleChangeWebhookUrl = async ({ webhookUrl }) => {
    await api.post(PartnerEndpoints.editWebhook, { webhook_url: webhookUrl });
    await refetch();
  };

  const handleGenerateCredentials = async () => {
    setGenerating(true);
    try {
      await api.post(PartnerEndpoints.generateCredentials, {});
      await refetch();
    } catch {
      setError('Could not generate API credentials. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // The details endpoint omits any field the caller lacks permission to see, while a
  // present-but-null field means "not set yet" — so visibility tests `in`, never truthiness.
  const canSeeWebhook = 'webhook_url' in details;
  const canSeeWebhookSecret = 'webhook_secret' in details;
  const canSeeIps = 'whitelisted_ips' in details;
  const canSeeCredentials = 'api_key' in details;

  const canUpdateWebhook = hasPermission(Permissions.webhookUpdate);
  const canUpdateCredentials = hasPermission(Permissions.credentialsUpdate);

  // `partner.settings.*` is matched by a team-only role, which can reach this route without
  // being shown any of the four sections below.
  const hasAnySection = canSeeWebhook || canSeeWebhookSecret || canSeeIps || canSeeCredentials;

  return (
    <>
      <PageHeader
        title="Developer"
        breadcrumbs={[{ label: 'Home', to: '/layout/dashboard' }, { label: 'Developer' }]}
      />

      {error && (
        <Alert variant="danger" className="mb-6" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <div className="grid grid-cols-12 gap-6">
        {canSeeWebhook && (
          <Card title="Webhook" className="col-span-12 mb-0 h-full xl:col-span-6">
            {details.webhook_url ? (
              <WebHooks
                loading={loading}
                url={details.webhook_url}
                onEditClick={canUpdateWebhook ? () => setWebhookModalOpen(true) : undefined}
              />
            ) : (
              <EmptyState
                icon={<Webhook className="h-8 w-8" aria-hidden="true" />}
                title="No webhook URL"
                message="Set a URL and we will post transaction events to it as they happen."
                action={
                  canUpdateWebhook ? (
                    <Button size="sm" onClick={() => setWebhookModalOpen(true)}>
                      Set webhook URL
                    </Button>
                  ) : undefined
                }
              />
            )}
          </Card>
        )}

        {canSeeWebhookSecret && (
          <Card title="Webhook signing secret" className="col-span-12 mb-0 h-full xl:col-span-6">
            <WebhookSecret
              secret={details.webhook_secret}
              canRotate={canUpdateWebhook}
              fetchDetails={refetch}
            />
          </Card>
        )}

        {canSeeCredentials && (
          <Card title="API credentials" className="col-span-12 mb-0">
            {details.api_key ? (
              <ApiSettings
                partnerDetails={details}
                canRotate={canUpdateCredentials}
                fetchDetails={refetch}
              />
            ) : (
              <EmptyState
                icon={<KeyRound className="h-8 w-8" aria-hidden="true" />}
                title="No API credentials"
                message="Generate a key and secret to start calling the CashAfrica API."
                action={
                  canUpdateCredentials ? (
                    <Button size="sm" loading={generating} onClick={handleGenerateCredentials}>
                      Generate credentials
                    </Button>
                  ) : undefined
                }
              />
            )}
          </Card>
        )}

        {canSeeIps && (
          <Card title="Whitelisted IP addresses" className="col-span-12 mb-0">
            <WhitelistedIps ips={details.whitelisted_ips ?? []} fetchDetails={refetch} />
          </Card>
        )}
      </div>

      {!hasAnySection && !error && (
        <Card className="mt-6">
          {loading ? (
            <>
              <Skeleton h="1rem" className="mb-3" />
              <Skeleton h="1rem" w="60%" />
            </>
          ) : (
            <EmptyState
              icon={<Braces className="h-8 w-8" aria-hidden="true" />}
              title="Nothing to configure here"
              message="Your role does not include access to webhooks, IP whitelisting or API credentials."
            />
          )}
        </Card>
      )}

      <EditWebhookModal
        isOpen={webhookModalOpen}
        webhook={details.webhook_url || ''}
        onClose={() => setWebhookModalOpen(false)}
        onSubmit={handleChangeWebhookUrl}
      />
    </>
  );
};

export default Developer;
